// Capture + composite + encode.
//
// Screen and both use a 1920x1080 landscape canvas; camera-only and dual
// camera preserve the primary camera's native frame shape.
// canvas.captureStream(30) provides the video track; an AudioContext mixes
// mic (+ screen/tab audio when granted) into a single track. MediaRecorder
// encodes with ~1s timeslice; chunks flow to the PartUploader.
//
// Acquisition (native pickers) is split from starting the encoder so the app
// layer can insert the watch-link mint + a 3-2-1 countdown in between:
// acquireDisplayStream() -> acquireUserStream() -> prepareRecording() ->
// (countdown, live preview already visible) -> session.begin().

export type CaptureMode = 'screen' | 'camera' | 'both' | 'dual';

export interface DisplayAcquireOptions {
  /** Hint only — browsers own the final screen/window/tab choice. */
  displaySurface?: 'monitor' | 'window' | 'browser';
}

export interface UserAcquireOptions {
  cameraEnabled: boolean;
  microphoneEnabled: boolean;
  cameraDeviceId?: string;
  cameraFacing?: 'user' | 'environment';
  microphoneDeviceId?: string;
}

export interface RecorderOptions {
  captureMode: CaptureMode;
  videoBitsPerSecond?: number;
  /** Test hook: paint animated noise onto the canvas so the encoder has
   * real work to do (used by e2e to guarantee byte volume). Real pipeline —
   * canvas -> captureStream -> MediaRecorder — just a busy screen. */
  noiseOverlay?: boolean;
  onChunk: (blob: Blob) => void;
  onError: (err: Error) => void;
}

export interface RecorderSession {
  container: 'webm' | 'mp4';
  mimeType: string;
  canvas: HTMLCanvasElement;
  /** Live preview stream of the composited output. */
  previewStream: MediaStream;
  hasCamera: boolean;
  hasAudio: boolean;
  /** Starts the actual MediaRecorder timeline. Call after any countdown. */
  begin(): void;
  pause(): void;
  resume(): void;
  isPaused(): boolean;
  elapsedS(): number;
  stop(): Promise<number>; // resolves with duration in seconds after final chunk flushed
  replaceCameraStream(stream: MediaStream): Promise<void>;
  setDualCameraStream(stream: MediaStream | null): Promise<void>;
  /** Swaps which of the two already-live dual-camera tracks renders
   * full-frame vs. bubble, in place — no getUserMedia, no track stop/start.
   * See the implementation's comment for why replaceCameraStream()/
   * setDualCameraStream() aren't reused for this directly. */
  swapDualCameraStreams(): Promise<void>;
  setBubblePosition(xFraction: number, yFraction: number): void;
  setBubbleScale(scale: number): void;
  startedAt: number;
  onExternalStop(cb: () => void): void;
}

const LANDSCAPE_W = 1920;
const LANDSCAPE_H = 1080;
const PORTRAIT_W = 1080;
const PORTRAIT_H = 1920;
const FPS = 30;

function cameraCanvasSize(stream: MediaStream | null): { width: number; height: number; cropToFill: boolean } {
  const settings = stream?.getVideoTracks()[0]?.getSettings();
  const width = settings?.width;
  const height = settings?.height;
  if (!width || !height) return { width: PORTRAIT_W, height: PORTRAIT_H, cropToFill: true };

  if (width < height) return { width: PORTRAIT_W, height: PORTRAIT_H, cropToFill: true };

  const scale = LANDSCAPE_W / Math.max(width, height);
  return { width: Math.round(width * scale), height: Math.round(height * scale), cropToFill: false };
}

/** Thrown by pickMimeType() when nothing in the candidate list is usable.
 * Message is shown to the user as-is (via app.ts's setDeviceStatus/control
 * error state), so it stays plain-language rather than naming codecs. */
const UNSUPPORTED_MESSAGE = "Recording isn't supported in this browser. Try the latest Chrome, Firefox, Edge, or Safari.";

/** isTypeSupported() is the documented predictor of what MediaRecorder can
 * produce, but it is only a predictor — browsers have shipped mismatches
 * where it approves a codec string that then throws at construction. A
 * throwaway 2x2 canvas track is enough to prove real constructibility:
 * MediaRecorder validates the container/codec profile itself, it doesn't
 * require the probe stream's tracks to already match every codec named in
 * the mimeType (e.g. a video-only probe is fine for a "vp9,opus" string).
 * Probing here, before prepareRecording() builds the real composed stream,
 * keeps the mimeType decision single-sourced: the exact string reported to
 * the server in POST /api/recordings (for the live viewer's MSE
 * addSourceBuffer) is guaranteed to be the one MediaRecorder actually ends
 * up using, which a fallback-after-the-fact at the real construction site
 * could not guarantee. */
function canActuallyConstruct(mimeType: string): boolean {
  let probeStream: MediaStream | null = null;
  try {
    const probeCanvas = document.createElement('canvas');
    probeCanvas.width = 2;
    probeCanvas.height = 2;
    probeStream = probeCanvas.captureStream(0);
    new MediaRecorder(probeStream, { mimeType });
    return true;
  } catch {
    return false;
  } finally {
    probeStream?.getTracks().forEach((t) => t.stop());
  }
}

/** hasAudio must reflect the REAL composed stream (mic/tab-audio track
 * actually acquired), not just a preference toggle. A mismatch here — this
 * function claiming an "opus" audio codec that the actual container never
 * gets an audio track for (e.g. a camera-only recording with the
 * microphone off or its permission denied) — doesn't fail MediaRecorder
 * construction. It instead produces a WebM init segment silently missing
 * the audio track the codec string promised, which is only fatal later:
 * a live viewer's MediaSource.addSourceBuffer(mimeType) accepts it, but the
 * *second* appendBuffer throws CHUNK_DEMUXER_ERROR_APPEND_FAILED
 * ("Initialization segment misses expected opus track"), permanently
 * closing that viewer's MediaSource. Discovered empirically: no existing
 * test ever opened /w/:slug during an active no-mic recording. */
function pickMimeType(hasAudio: boolean): { mimeType: string; container: 'webm' | 'mp4' } {
  if (typeof MediaRecorder === 'undefined') throw new Error(UNSUPPORTED_MESSAGE);
  // vp9 first: better compression than vp8 at equal quality, which matters
  // here since Clip uploads continuously while recording (smaller chunks,
  // less upload volume over the session). MediaRecorder's real-time encode
  // path uses libvpx's realtime speed profile (not the slow offline/quality
  // profile), and Chromium browsers hardware-accelerate vp9 encode on most
  // GPUs from ~2020 onward, so this doesn't trade away responsiveness on the
  // target browsers. Where it *is* too slow, degradation is graceful (frame
  // drops in the recording, not a construction failure), so a struggling
  // machine still gets a working recording rather than needing vp8 to be
  // first. vp8 stays as the fallback for anything that can't do vp9.
  const candidates: { mimeType: string; container: 'webm' | 'mp4' }[] = hasAudio
    ? [
        { mimeType: 'video/webm;codecs=vp9,opus', container: 'webm' },
        { mimeType: 'video/webm;codecs=vp8,opus', container: 'webm' },
        { mimeType: 'video/webm', container: 'webm' },
        { mimeType: 'video/mp4', container: 'mp4' }, // Safari 14.1+
      ]
    : [
        { mimeType: 'video/webm;codecs=vp9', container: 'webm' },
        { mimeType: 'video/webm;codecs=vp8', container: 'webm' },
        { mimeType: 'video/webm', container: 'webm' },
        { mimeType: 'video/mp4', container: 'mp4' }, // Safari 14.1+
      ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c.mimeType) && canActuallyConstruct(c.mimeType)) return c;
  }
  throw new Error(UNSUPPORTED_MESSAGE);
}

export function detectContainer(hasAudio: boolean): 'webm' | 'mp4' {
  return pickMimeType(hasAudio).container;
}

/** The exact MediaRecorder mimeType string (e.g. "video/webm;codecs=vp9,opus"),
 * sent to the server on POST /api/recordings so a live viewer's
 * MediaSource.addSourceBuffer() can use the same codec string this browser
 * actually encoded with — the bare container alone isn't enough for MSE.
 * hasAudio must reflect the real acquired stream — see pickMimeType(). */
export function detectMimeType(hasAudio: boolean): string {
  return pickMimeType(hasAudio).mimeType;
}

/** Source video elements must be real (attached, rendered) DOM nodes for
 * browsers to keep decoding frames reliably for the lifetime of a long
 * recording — a fully detached `<video>` is where the old "preview doesn't
 * work" bug came from. Visually hidden via off-screen position, not
 * `display:none` (which some browsers use as a signal to pause decode). */
async function attachVideo(stream: MediaStream): Promise<HTMLVideoElement> {
  const v = document.createElement('video');
  v.muted = true;
  v.playsInline = true;
  v.srcObject = stream;
  v.style.position = 'fixed';
  v.style.left = '-9999px';
  v.style.top = '0';
  v.style.width = '2px';
  v.style.height = '2px';
  v.style.opacity = '0';
  v.style.pointerEvents = 'none';
  document.body.appendChild(v);
  await v.play();
  return v;
}

/** Opens the browser-native display picker. Rejects if the user cancels. */
export async function acquireDisplayStream(opts: DisplayAcquireOptions = {}): Promise<MediaStream> {
  const video: MediaTrackConstraints & { displaySurface?: string } = { frameRate: FPS };
  if (opts.displaySurface) video.displaySurface = opts.displaySurface;
  return navigator.mediaDevices.getDisplayMedia({ video, audio: true });
}

/** Acquires camera/mic per the resolved preference. Never throws — a denial
 * or missing device just means the caller falls back (screen-only, or a
 * blocking error for camera-only mode, decided by the app layer). */
export async function acquireUserStream(opts: UserAcquireOptions): Promise<MediaStream | null> {
  if (!opts.cameraEnabled && !opts.microphoneEnabled) return null;
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: opts.cameraEnabled
        ? {
            deviceId: opts.cameraFacing ? undefined : opts.cameraDeviceId ? { exact: opts.cameraDeviceId } : undefined,
            facingMode: opts.cameraFacing ? { ideal: opts.cameraFacing } : undefined,
            width: 640,
            height: 480,
          }
        : false,
      audio: opts.microphoneEnabled
        ? { deviceId: opts.microphoneDeviceId ? { exact: opts.microphoneDeviceId } : undefined }
        : false,
    });
  } catch (err) {
    console.warn('[clip] camera/mic unavailable', err);
    return null;
  }
}

export function prepareRecording(
  screenStream: MediaStream | null,
  camStream: MediaStream | null,
  secondCamStream: MediaStream | null,
  opts: RecorderOptions,
): RecorderSession {
  let mode = opts.captureMode;
  const cameraSize = cameraCanvasSize(camStream);
  const CANVAS_W = mode === 'camera' || mode === 'dual' ? cameraSize.width : LANDSCAPE_W;
  const CANVAS_H = mode === 'camera' || mode === 'dual' ? cameraSize.height : LANDSCAPE_H;
  const cropPrimaryToFill = (mode === 'camera' || mode === 'dual') && cameraSize.cropToFill;
  // 1. Composite canvas — built immediately so the app layer can preview it
  // during the countdown, before the encoder actually starts.
  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;
  const ctx = canvas.getContext('2d', { alpha: false })!;

  const BASE_BUBBLE_RADIUS = 140;
  // Kept in sync with client/app.ts's own default -- see the comment there
  // for why 0.86 was replaced (it overlapped the preview-controls bar).
  let bubblePosition = { x: 0.86, y: 0.35 };
  let bubbleScale = 1;

  const bubbleGeometry = () => {
    const radius = BASE_BUBBLE_RADIUS * bubbleScale;
    const minimumX = Math.min(0.5, radius / 2 / CANVAS_W);
    const minimumY = Math.min(0.5, radius / 2 / CANVAS_H);
    const x = Math.min(1 - minimumX, Math.max(minimumX, bubblePosition.x));
    const y = Math.min(1 - minimumY, Math.max(minimumY, bubblePosition.y));
    return { radius, x: x * CANVAS_W, y: y * CANVAS_H };
  };

  let stopped = false;
  let screenVideo: HTMLVideoElement | null = null;
  let camVideo: HTMLVideoElement | null = null;
  let camVideo2: HTMLVideoElement | null = null;
  const detachers: (() => void)[] = [];

  const drawFullFrame = (video: HTMLVideoElement, fit: 'contain' | 'cover' = 'contain') => {
    const vw = video.videoWidth || CANVAS_W;
    const vh = video.videoHeight || CANVAS_H;
    const scale = fit === 'cover'
      ? Math.max(CANVAS_W / vw, CANVAS_H / vh)
      : Math.min(CANVAS_W / vw, CANVAS_H / vh);
    const dw = vw * scale;
    const dh = vh * scale;
    ctx.drawImage(video, (CANVAS_W - dw) / 2, (CANVAS_H - dh) / 2, dw, dh);
  };

  const drawBubble = (video: HTMLVideoElement) => {
    const { radius, x, y } = bubbleGeometry();
    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.clip();
    const cw = video.videoWidth || 640;
    const ch = video.videoHeight || 480;
    const cScale = Math.max((radius * 2) / cw, (radius * 2) / ch);
    ctx.drawImage(
      video,
      x - (cw * cScale) / 2,
      y - (ch * cScale) / 2,
      cw * cScale,
      ch * cScale,
    );
    ctx.restore();
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.lineWidth = 8;
    ctx.strokeStyle = `hsl(${((Date.now() % 28000) / 28000) * 360} 76% 54%)`;
    ctx.stroke();
  };

  const paint = () => {
    if (stopped) return;
    ctx.fillStyle = '#101014';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    const primary = mode === 'camera' || mode === 'dual' ? camVideo : screenVideo;
    if (primary && primary.videoWidth > 0) drawFullFrame(primary, cropPrimaryToFill ? 'cover' : 'contain');

    if (opts.noiseOverlay) {
      for (let i = 0; i < 60; i++) {
        ctx.fillStyle = `hsl(${(Math.random() * 360) | 0} 80% 50% / 0.35)`;
        ctx.fillRect(Math.random() * CANVAS_W, Math.random() * CANVAS_H, 90, 60);
      }
    }

    if (mode === 'both' && camVideo && camVideo.videoWidth > 0) drawBubble(camVideo);
    if (mode === 'dual' && camVideo2 && camVideo2.videoWidth > 0) drawBubble(camVideo2);
  };
  // setInterval (not rAF) so painting survives background-tab throttling
  // better; rAF stops entirely when hidden, setInterval degrades to ~1 Hz.
  const paintTimer = window.setInterval(paint, 1000 / FPS);

  const camHasVideo = !!camStream?.getVideoTracks().length;
  let currentCamStream: MediaStream | null = camStream;
  const readyPromise = (async () => {
    if (screenStream) {
      screenVideo = await attachVideo(screenStream);
      detachers.push(() => screenVideo?.remove());
    }
    if (camHasVideo) {
      camVideo = await attachVideo(new MediaStream(camStream!.getVideoTracks()));
      detachers.push(() => camVideo?.remove());
    }
    if (secondCamStream?.getVideoTracks().length) {
      camVideo2 = await attachVideo(new MediaStream(secondCamStream.getVideoTracks()));
      detachers.push(() => camVideo2?.remove());
    }
  })();

  // 2. Audio mix (safe to build ahead of begin(); AudioContext starts
  // "running" only once a user gesture already unlocked it upstream).
  const audioCtx = new AudioContext();
  const dest = audioCtx.createMediaStreamDestination();
  let haveAudio = false;
  if (camStream?.getAudioTracks().length) {
    audioCtx.createMediaStreamSource(new MediaStream(camStream.getAudioTracks())).connect(dest);
    haveAudio = true;
  }
  if (screenStream?.getAudioTracks().length) {
    audioCtx.createMediaStreamSource(new MediaStream(screenStream.getAudioTracks())).connect(dest);
    haveAudio = true;
  }

  const canvasStream = canvas.captureStream(FPS);
  const tracks = [...canvasStream.getVideoTracks(), ...(haveAudio ? dest.stream.getAudioTracks() : [])];
  const composedStream = new MediaStream(tracks);
  const { mimeType, container } = pickMimeType(haveAudio);
  const recorder = new MediaRecorder(composedStream, {
    mimeType,
    videoBitsPerSecond: opts.videoBitsPerSecond ?? 4_000_000,
    audioBitsPerSecond: 128_000,
  });
  let totalPausedMs = 0;
  let pauseStartedAt: number | null = null;

  recorder.ondataavailable = (ev) => {
    if (ev.data && ev.data.size > 0) opts.onChunk(ev.data);
  };
  recorder.onerror = (ev) => {
    opts.onError(new Error(`MediaRecorder error: ${(ev as any).error ?? 'unknown'}`));
  };

  let externalStopCb: (() => void) | null = null;
  // A display-track ending (screen share stopped from browser UI) always
  // ends the recording. In camera-only mode there's no display track; a
  // camera track ending (device unplugged) is the equivalent safety net.
  const bindCameraEnd = (stream: MediaStream) => {
    stream.getVideoTracks()[0]?.addEventListener('ended', () => {
      if ((mode === 'camera' || mode === 'dual') && currentCamStream === stream) externalStopCb?.();
    });
  };
  if (screenStream) {
    screenStream.getVideoTracks()[0]?.addEventListener('ended', () => externalStopCb?.());
  } else if (camStream) {
    bindCameraEnd(camStream);
  }

  const cleanup = () => {
    stopped = true;
    window.clearInterval(paintTimer);
    for (const t of [
      ...(screenStream?.getTracks() ?? []),
      ...(camStream?.getAudioTracks() ?? []),
      ...(currentCamStream?.getVideoTracks() ?? []),
      ...(secondCamStream?.getTracks() ?? []),
    ]) t.stop();
    for (const detach of detachers) detach();
    void audioCtx.close();
  };

  const session: RecorderSession = {
    container,
    mimeType,
    canvas,
    previewStream: canvasStream,
    hasCamera: camHasVideo,
    hasAudio: haveAudio,
    startedAt: 0,
    begin() {
      void readyPromise; // canvas already painting from acquisition; begin() only starts the encoder timeline
      session.startedAt = Date.now();
      recorder.start(1000); // 1s timeslice
    },
    pause() {
      if (recorder.state !== 'recording') return;
      recorder.pause();
      pauseStartedAt = Date.now();
    },
    resume() {
      if (recorder.state !== 'paused') return;
      recorder.resume();
      if (pauseStartedAt !== null) totalPausedMs += Date.now() - pauseStartedAt;
      pauseStartedAt = null;
    },
    isPaused() {
      return recorder.state === 'paused';
    },
    elapsedS() {
      if (!session.startedAt) return 0;
      const now = Date.now();
      const currentPauseMs = pauseStartedAt === null ? 0 : now - pauseStartedAt;
      return (now - session.startedAt - totalPausedMs - currentPauseMs) / 1000;
    },
    stop(): Promise<number> {
      return new Promise((resolve, reject) => {
        if (recorder.state === 'inactive') {
          cleanup();
          resolve(session.elapsedS());
          return;
        }
        recorder.onstop = () => {
          cleanup();
          resolve(session.elapsedS());
        };
        try {
          recorder.stop(); // fires a final dataavailable before onstop
        } catch (err) {
          cleanup();
          reject(err);
        }
      });
    },
    async replaceCameraStream(stream: MediaStream): Promise<void> {
      if (stream.getVideoTracks().length === 0) throw new Error('Camera stream is unavailable.');
      const oldStream = camVideo?.srcObject as MediaStream | null;
      if (!camVideo) {
        camVideo = await attachVideo(new MediaStream(stream.getVideoTracks()));
        detachers.push(() => camVideo?.remove());
      } else {
        camVideo.srcObject = new MediaStream(stream.getVideoTracks());
        try {
          await camVideo.play();
        } catch (err) {
          camVideo.srcObject = oldStream;
          void camVideo.play();
          throw err;
        }
      }
      currentCamStream = stream;
      bindCameraEnd(stream);
      oldStream?.getTracks().forEach((track) => track.stop());
    },
    async setDualCameraStream(stream: MediaStream | null): Promise<void> {
      if (stream) {
        if (!camVideo || stream.getVideoTracks().length === 0) throw new Error('Camera stream is unavailable.');
        const oldStream = camVideo2?.srcObject as MediaStream | null;
        if (!camVideo2) {
          camVideo2 = await attachVideo(new MediaStream(stream.getVideoTracks()));
          detachers.push(() => camVideo2?.remove());
        } else {
          camVideo2.srcObject = new MediaStream(stream.getVideoTracks());
          await camVideo2.play();
        }
        oldStream?.getTracks().forEach((track) => track.stop());
        secondCamStream = stream;
        mode = 'dual';
      } else {
        const oldStream = camVideo2?.srcObject as MediaStream | null;
        camVideo2?.remove();
        camVideo2 = null;
        oldStream?.getTracks().forEach((track) => track.stop());
        secondCamStream = null;
        mode = 'camera';
      }
    },
    // Swaps the two live dual-camera tracks in place. replaceCameraStream()
    // and setDualCameraStream() both stop() the track they displace -- the
    // right behavior when a caller hands in a genuinely new device stream
    // (device picker, camera flip), but wrong here: both physical cameras
    // are still live and wanted, only their full-frame/bubble roles are
    // trading places. Stopping either would silently kill that camera's
    // hardware for the rest of the recording, forcing a slow re-acquire
    // (and risking the exact hardware-contention failure SC-35 fixed) the
    // next time the user taps to swap back.
    async swapDualCameraStreams(): Promise<void> {
      if (mode !== 'dual' || !camVideo || !camVideo2 || !secondCamStream) {
        throw new Error('Dual camera streams are unavailable.');
      }
      const newPrimaryStream = secondCamStream;
      const newSecondaryStream = currentCamStream;
      const primarySrc = camVideo.srcObject;
      const secondarySrc = camVideo2.srcObject;
      camVideo.srcObject = secondarySrc;
      camVideo2.srcObject = primarySrc;
      try {
        await Promise.all([camVideo.play(), camVideo2.play()]);
      } catch (err) {
        camVideo.srcObject = primarySrc;
        camVideo2.srcObject = secondarySrc;
        throw err;
      }
      currentCamStream = newPrimaryStream;
      secondCamStream = newSecondaryStream;
      // The "external stop" safety net (device unplugged) is only ever
      // watched on whichever stream is CURRENTLY primary -- rebind it to
      // the stream that just became primary so an unplug of the new
      // full-frame camera still ends the recording, not the one now
      // demoted to the bubble.
      bindCameraEnd(currentCamStream);
    },
    setBubblePosition(xFraction: number, yFraction: number): void {
      bubblePosition = {
        x: Math.min(1, Math.max(0, xFraction)),
        y: Math.min(1, Math.max(0, yFraction)),
      };
    },
    setBubbleScale(scale: number): void {
      bubbleScale = Math.min(2.5, Math.max(0.5, scale));
    },
    onExternalStop(cb: () => void) {
      externalStopCb = cb;
    },
  };
  return session;
}
